import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Briefcase, Heart, Lightbulb } from 'lucide-react';
import CommunityLayout from "../components/layouts/CommunityLayout";
import { usePageTitle } from '../hooks/usePageTitle';
import WefayoAIChatInterface from '../components/wefayoAI/ChatInterface';
import { getSessionData } from '../utils/auth';

// Mirrors the agents defined in ChatInterface.js (kept separate to avoid editing the dialog)
const agents = [
    {
        id: 'conversationist',
        name: 'Conversation Coach',
        icon: MessageCircle,
        description: 'Engage in meaningful conversations and discussions',
        details: "Practice writing, talking, asking questions, and role playing conversations before doing them or just chat about anything on your mind",
    },
    {
        id: 'companionist',
        name: 'Companion',
        icon: Heart,
        description: 'Your friendly companion for emotional support',
        details: "Talk about your day, feelings, anxiety, sensory overload, or anything else weighing on you; a safe space to share, vent, and find calming support",
    },
    {
        id: 'careerist',
        name: 'Career Support',
        icon: Briefcase,
        description: 'Explore job and career topics',
        details: "Get help with your resume, interview preparations, or tricky work situations",
    },
    {
        id: 'clarifyist',
        name: 'Organizer',
        icon: Lightbulb,
        description: 'Simplify your tasks and routines',
        details: "Get help breaking down tasks, understanding steps, managing daily routines, and making things easier to do",
    },
];

const AskWefayoLanding = () => {
    usePageTitle('Ask Wefayo AI - Wefayo');
    const navigate = useNavigate();
    const [selectedAgentId, setSelectedAgentId] = useState(null);

    useEffect(() => {
        if (getSessionData() === null) {
            navigate('/login');
        }
    }, [navigate]);

    const handleStartChat = (agentId) => {
        setSelectedAgentId(agentId);
    }

    return (
        <CommunityLayout
            currentPage="ai-chat"
            onCommunitiesLoaded={() => { }}
            isFullPageWidth={true}
            overflow="overflow-y-hidden"
            px="px-0"
        >
            {/* ── Full-width gradient wrapper ──────────────────────────────────── */}
            <div
                className="relative w-full flex pt-0 md:pt-[5px] lg:pt-[57px] h-[calc(100vh-57px)] overflow-y-auto"
                style={{ background: 'linear-gradient(135deg, #2e0454 0%, #581c87 55%, #7c3aed 100%)' }}
            >
                {/* Subtle dot-grid overlay */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-10"
                    style={{
                        backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                        backgroundSize: '30px 30px',
                    }}
                />

                {/* ── Two-column layout ──────────────────────────────────────────── */}
                <div className="relative flex flex-col lg:flex-row items-start justify-between gap-10 px-6 md:px-12 lg:px-16 py-16 md:py-20 mx-auto max-w-8xl xl:max-w-[1500px]">

                    {/* ── LEFT — Intro / hero copy ───────────────────────────────── */}
                    <div className="h-full flex flex-col items-start justify-center text-left lg:max-w-[40%] lg:sticky lg:top-8">



                        {/* Icon ring */}
                        <div className="hidden lg:flex w-16 h-16 rounded-full bg-white/10 border border-white/20  items-center justify-center mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-8 h-8">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                            </svg>
                        </div>

                        {/* Heading */}
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight flex flex-row items-start lg:items-center  ">
                            <div className="lg:hidden !w-16 !h-16 flex-shrink-0 rounded-full bg-white/10 border border-white/20 flex items-center justify-center ">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                </svg>
                            </div>
                            <div className="pl-2 lg:pl-0 ">
                                When you're not sure what to do,{' '}
                                {/* <span className="italic text-purple-300">Wefayo AI</span>{' '} */}
                                we're here to talk.
                            </div>
                        </h1>

                        {/* Description */}
                        <p className="text-white/80 text-base ">
                            Personalized AI companions to help you communicate, grow your career, and navigate everyday life &mdash; completely free.
                        </p>

                        {/* Badge */}
                        <div className="mt-5 lg:mt-20 self-start inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-white/90 text-xs font-semibold mb-6 tracking-widest uppercase">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-300 inline-block" />
                            AI-POWERED &middot; FREE &middot; SUPPORTIVE
                        </div>

                        {/* Assistant type pills
                        <div className="flex flex-wrap gap-2 mb-10">
                            {agents.map((a) => (
                                <span
                                    key={a.id}
                                    className="bg-white/10 border border-white/25 text-white text-xs px-3 py-1 rounded-full"
                                >
                                    {a.name}
                                </span>
                            ))}
                        </div> */}

                        {/* CTA buttons */}
                        {/* <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleStartChat}
                                className="flex items-center justify-center gap-2 bg-white text-purple-900 font-bold px-7 py-3 rounded-full hover:bg-purple-50 transition-colors shadow-lg"
                            >
                                Start Chatting &rarr;
                            </button>
                        </div> */}

                        {/* <p className="text-white/40 text-xs mt-5">
                            All assistants are free to use. No account required to explore.
                        </p> */}
                    </div>

                    {/* ── RIGHT — Assistant cards ────────────────────────────────── */}
                    <div className="flex flex-col w-full lg:max-w-[60%] items-center justify-center md:h-full">

                        <h2 className="text-xl font-bold text-white text-center mb-1">
                            Select an assistant to begin
                        </h2>
                        <p className="text-white/60 text-sm text-center mb-6">
                            Designed to support you, wherever you are.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {agents.map((agent) => (
                                <button
                                    key={agent.id}
                                    onClick={() => handleStartChat(agent.id)}
                                    className="flex flex-col items-center p-5 rounded-xl border-2 border-white/20 bg-white/5 hover:bg-white/15 hover:border-purple-300 hover:shadow-lg transition-all duration-200 group text-center"
                                >
                                    <div className="bg-white/10 rounded-full p-3 mb-3 group-hover:bg-purple-500 transition-colors duration-200">
                                        <agent.icon className="h-8 w-8 text-purple-200 group-hover:text-white transition-colors duration-200" />
                                    </div>
                                    <h3 className="font-bold text-white text-lg mb-1">{agent.name}</h3>
                                    <p className="text-md text-purple-200 font-semibold mb-2">{agent.description}</p>
                                    <p className="text-sm text-white/50">{agent.details}</p>
                                </button>
                            ))}
                        </div>

                        <p className="text-white/30 text-sm text-center mt-6 w-full mx-auto">
                            {/* For informational purposes only. Not a substitute for professional advice or support. */}

                            The communication offered here is for informational purposes only. Some terms used may be nonstandard or informal. This is not a substitute for professional, individualized advice or support. If you have specific needs or concerns, please consult a qualified professional or trusted support network
                        </p>
                    </div>

                </div>
            </div>

            {selectedAgentId && (
                <WefayoAIChatInterface selectedAgentId={selectedAgentId} isModal={true} isOpen={true} closeFun={() => setSelectedAgentId(null)} />
            )}
        </CommunityLayout>
    );
};

export default AskWefayoLanding;
